import React, { FC, useMemo } from "react";
import { UserTotals } from "components/Analytics/Totals";
import { useGetUserAnalyticsQuery } from "generated/graphql";
import { Moment } from "moment";
import { Data, Graph } from "../Users/Graph";
import { PageAlert } from "@xapp/components";

export interface AppAnalyticsContainerProps {
    readonly appId: string;
    readonly organizationId?: string;
    readonly startDate: Date | Moment;
    readonly endDate: Date | Moment;
}

export interface IntervalsApp {
    isoDate: string;
    interval: string;
    newUsers: number;
    returningUsers: number;
}

export interface UserApp {
    intervals: IntervalsApp[],
    newUsers: number,
    totalSessions: number,
    totalUsers: number
};

export const convertData = (appId: string, analytics: UserApp): Data[] => {
    if (!analytics || !analytics.intervals || analytics.intervals.length === 0) {
        return [];
    }

    const appData: Data[] = analytics.intervals.map((interval: IntervalsApp) => {
        return {
            appId: appId,
            date: new Date(interval.isoDate).toLocaleDateString(),
            newUsers: interval.newUsers,
            returningUsers: interval.returningUsers,
            total: interval.newUsers + interval.returningUsers,
        };
    });

    return appData;
}

export const AppAnalyticsContainer: FC<AppAnalyticsContainerProps> = ({ appId, startDate, endDate }: AppAnalyticsContainerProps) => {

    const [response] = useGetUserAnalyticsQuery({
        pause: !appId,
        variables: {
            appId,
            endDate: endDate.toISOString(),
            startDate: startDate.toISOString()
        }
    });

    const { data, error } = response;

    const totals = data?.app?.analytics?.user;
    const intervals = data?.app?.analytics?.user?.intervals;

    const totalsNew = {
        newUsers: totals?.newUsers,
        totalSessions: totals?.totalSessions,
        totalUsers: totals?.totalUsers,
    }

    const dataFinal = useMemo(() => {
        const newData: UserApp = {
            intervals: intervals ? [...intervals] : [],
            newUsers: totals?.newUsers,
            totalSessions: totals?.totalSessions,
            totalUsers: totals?.totalUsers
        }
        return convertData(appId, newData);
    }, [appId, intervals, totals?.newUsers, totals?.totalSessions, totals?.totalUsers]);

    if (error) {
        return (
            <PageAlert background="red">
                <div>
                    Error loading analytics, please try again later
                </div>
            </PageAlert>
        );
    }

    return (
        <>
            <UserTotals   {...totalsNew} />
            <div id="app_users_analytics_graph" >
                <div >
                    <h2 >New &amp; Returning Users Per Day</h2>
                    {
                        dataFinal && dataFinal.length !== 0 && <Graph height={500} data={dataFinal} margin={undefined} />
                    }
                </div>
            </div>
        </>
    );
};

